/**
 * Lesson media helpers.
 *
 * Instructors paste whatever URL they have in the address bar — a YouTube
 * watch link, a youtu.be share link, a Vimeo page, a Google Drive preview.
 * None of those render in an iframe as-is, so each provider is normalised to
 * its embeddable form. Anything we don't recognise is passed through only if
 * it is a plain https URL, so a pasted `javascript:` payload can never reach
 * an iframe src.
 */

const YOUTUBE_ID = /^[\w-]{11}$/;

function safeUrl(raw) {
  const text = String(raw || '').trim();
  if (!text) return null;
  try {
    const url = new URL(text);
    // Only ever hand http(s) to an iframe/video src.
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url;
  } catch {
    return null;
  }
}

// Each provider returns `undefined` for "not my host" so dispatch moves on, or
// `null` for "my host but I could not find a video id". The null case must not
// fall through to raw passthrough: iframing a provider's watch page instead of
// its player just renders a blank box, because they all send X-Frame-Options.

function youtubeEmbed(url) {
  const host = url.hostname.replace(/^www\./, '');
  let id = '';
  if (host === 'youtu.be') {
    id = url.pathname.slice(1);
  } else if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
    if (url.pathname === '/watch') id = url.searchParams.get('v') || '';
    else if (url.pathname.startsWith('/embed/')) id = url.pathname.slice('/embed/'.length);
    else if (url.pathname.startsWith('/shorts/')) id = url.pathname.slice('/shorts/'.length);
    else if (url.pathname.startsWith('/live/')) id = url.pathname.slice('/live/'.length);
  } else {
    return undefined;
  }
  id = (id || '').split('/')[0];
  if (!YOUTUBE_ID.test(id)) return null;

  // Preserve a start-time deep link if the instructor copied one.
  const start = url.searchParams.get('t') || url.searchParams.get('start') || '';
  const seconds = String(start).match(/^(\d+)s?$/) ? String(start).replace(/s$/, '') : '';
  const query = seconds ? `?start=${seconds}` : '';
  return `https://www.youtube-nocookie.com/embed/${id}${query}`;
}

function vimeoEmbed(url) {
  const host = url.hostname.replace(/^www\./, '');
  if (host === 'player.vimeo.com') return url.href;
  if (host !== 'vimeo.com') return undefined;
  const segments = url.pathname.split('/').filter(Boolean);
  // Unlisted videos look like /123456789/abcdef — the hash is the second part.
  const id = segments[0];
  if (!/^\d+$/.test(id || '')) return null;
  const hash = segments[1] && /^[\w]+$/.test(segments[1]) ? `?h=${segments[1]}` : '';
  return `https://player.vimeo.com/video/${id}${hash}`;
}

function driveEmbed(url) {
  const host = url.hostname.replace(/^www\./, '');
  if (host !== 'drive.google.com') return undefined;
  const match = url.pathname.match(/\/file\/d\/([\w-]+)/);
  if (!match) return null;
  return `https://drive.google.com/file/d/${match[1]}/preview`;
}

function loomEmbed(url) {
  const host = url.hostname.replace(/^www\./, '');
  if (host !== 'loom.com') return undefined;
  const match = url.pathname.match(/\/(?:share|embed)\/([\w-]+)/);
  if (!match) return null;
  return `https://www.loom.com/embed/${match[1]}`;
}

const PROVIDERS = [youtubeEmbed, vimeoEmbed, driveEmbed, loomEmbed];

/**
 * Convert a pasted video link into something an iframe can render.
 * Returns null when the input is empty, not a safe http(s) URL, or is a known
 * provider link we could not read a video id out of.
 */
export function toEmbedUrl(raw) {
  const url = safeUrl(raw);
  if (!url) return null;
  for (const provider of PROVIDERS) {
    const result = provider(url);
    if (result !== undefined) return result; // null means "recognised but unusable"
  }
  return url.href;
}

/** True when the link points at a file we can play in a native <video> tag. */
export function isDirectVideoFile(raw) {
  const url = safeUrl(raw);
  if (!url) return false;
  return /\.(mp4|webm|ogg|mov|m4v)$/i.test(url.pathname);
}

/** Human label for the provider, used in the instructor editor's preview. */
export function describeVideoSource(raw) {
  const url = safeUrl(raw);
  if (!url) return '';
  const host = url.hostname.replace(/^www\./, '');
  if (host.includes('youtube') || host === 'youtu.be') return 'YouTube';
  if (host.includes('vimeo')) return 'Vimeo';
  if (host === 'drive.google.com') return 'Google Drive';
  if (host === 'loom.com') return 'Loom';
  if (isDirectVideoFile(raw)) return 'Direct video file';
  return host;
}

const UNITS = ['B', 'KB', 'MB', 'GB'];

/** Compact human file size, e.g. "2.4 MB". */
export function formatFileSize(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '';
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < UNITS.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size >= 10 || unit === 0 ? Math.round(size) : size.toFixed(1)} ${UNITS[unit]}`;
}

// Order matters: every OOXML type contains the substring "officedocument", so
// spreadsheets and decks must be matched before the word-processing pattern.
const MATERIAL_ICON_HINTS = [
  [/pdf/, 'pdf'],
  [/^image\//, 'image'],
  [/^audio\//, 'audio'],
  [/spreadsheet|excel|csv/, 'sheet'],
  [/presentation|powerpoint/, 'slides'],
  [/wordprocessing|msword/, 'doc'],
  [/zip|compressed/, 'archive'],
];

/** Coarse category for choosing a material's icon and colour. */
export function materialKind(contentType) {
  const type = String(contentType || '').toLowerCase();
  const hit = MATERIAL_ICON_HINTS.find(([pattern]) => pattern.test(type));
  return hit ? hit[1] : 'file';
}
